import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	Badge,
	Box,
	Button,
	Collapse,
	IconButton,
	Skeleton,
	Stack,
	Typography,
	useTheme,
} from '@mui/material';
import { TransitionGroup } from 'react-transition-group';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

const PolicyData = ({ policy }) => {
	return (
		<Typography variant='inherit' sx={{ fontSize: '13px' }}>
			<span style={{ marginRight: '0.25em' }}>
				{policy.classOfRisk ?? policy?.ClassOfRisk}
			</span>
			<span style={{ marginRight: '0.25em' }}>{' - '}</span>
			<span>{policy.policyNumber ?? policy?.PolicyNumber}</span>
		</Typography>
	);
};

const TransactionData = ({ policy }) => {
	return (
		<Typography variant='inherit' sx={{ fontSize: '13px' }}>
			<span style={{ display: 'inline-block' }}>
				<span style={{ marginRight: '0.25em' }}>{policy.invoiceNumber}</span>
				<span style={{ marginRight: '0.25em' }}>{' | '}</span>
				<span style={{ marginRight: '0.25em' }}>{policy.classOfRisk}</span>
			</span>
			<span>{policy.amount}</span>
		</Typography>
	);
};

const DataGridDealPolicies = ({
	policiesLoading,
	policies: rowPolicies,
	transactions: rowTransactions,
}) => {
	const theme = useTheme();
	const showTransactions = rowTransactions != null;
	const policies = Array.from(rowTransactions ?? rowPolicies ?? []);
	const hasPolicies = policies.length > 0;

	const [expanded, setExpanded] = useState(false);

	const shownPolicy = useMemo(() => policies.slice(0, 1)?.[0], [policies]);
	const hiddenPolicies = useMemo(() => policies.slice(1), [policies]);

	const expandPolicies = (event) => {
		event.preventDefault();
		event.stopPropagation();
		setExpanded((p) => !p);
	};

	const Icon = expanded ? ArrowDropUpIcon : MoreHorizIcon;

	const HiddenButton = useCallback(
		() => (
			<Box
				sx={{
					display: expanded ? 'flex' : 'inline',
					justifyContent: 'flex-end',
					alignItems: 'center',
					...(!expanded && { marginLeft: 1 }),
				}}
			>
				{!expanded && (
					<Typography
						variant='task_updated'
						sx={{ fontSize: '11px', marginRight: 0.25 }}
					>{`+${hiddenPolicies.length}`}</Typography>
				)}
				<IconButton onClick={expandPolicies} sx={{ fontSize: '13px', padding: 0 }}>
					<Icon sx={{ fontSize: '20px' }} />
				</IconButton>
			</Box>
		),
		[expanded, setExpanded, hiddenPolicies?.length],
	);

	const Component = showTransactions ? TransactionData : PolicyData;

	return policiesLoading ? (
		<Skeleton variant='text' width='5em' />
	) : hasPolicies ? (
		<Stack>
			<Collapse in={true} sx={{ '& .MuiTypography-root': { display: 'inline' } }}>
				<Component {...{ policy: shownPolicy }} />
				{!expanded && hiddenPolicies.length > 0 && <HiddenButton />}
			</Collapse>

			<TransitionGroup>
				{expanded &&
					hiddenPolicies.map((policy) => {
						return (
							<Collapse key={`policy=${policy.Id}`} in={expanded}>
								<Box marginTop={0.5}>
									<Component {...{ policy }} />
								</Box>
							</Collapse>
						);
					})}
			</TransitionGroup>
			<Collapse in={hiddenPolicies.length > 0 && expanded}>
				<HiddenButton />
			</Collapse>
		</Stack>
	) : (
		<Typography sx={{ fontSize: '13px', color: theme.palette.text.secondary }}>
			{`No ${showTransactions ? 'invoices' : 'policies'} linked`}
		</Typography>
	);
};

export default DataGridDealPolicies;
